/* eslint-disable sonarjs/cognitive-complexity */
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { ChartActionsComponentProps, ChartMetaFormField, MarDosageRecord } from 'models/ui';
import { chartHelper } from 'helpers';
import { Box, ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider, Sidebar } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { DOSE_MODE, FormField } from './constants';

export interface MarSidebarViewProps {
  openSidebar: boolean;
  sidebarProps: Function;
  selectedDosageRecord: MarDosageRecord;
  doseFormMode: string;
  formSubmittedCount: number;
  formFieldMap: Map<string, ChartMetaFormField>;
  chartActionsProps: ChartActionsComponentProps;
  onCloseClick: Function;
}
const MarDosageSidebarViewANZ = (props: MarSidebarViewProps) => {
  const { onCloseClick, chartActionsProps, formFieldMap, formSubmittedCount, selectedDosageRecord, doseFormMode, sidebarProps, openSidebar } = props;
  const [isShowCategory, setIsShowCategory] = useState(false);

  const drugName = selectedDosageRecord?.drugName;
  const route = selectedDosageRecord?.route;

  const dose = selectedDosageRecord?.dose;
  const frequency = selectedDosageRecord?.frequency;

  const specialInstructions = selectedDosageRecord?.specialInstructions;
  const isAdministered = selectedDosageRecord?.isAdministered;

  useEffect(() => {
    if (formFieldMap.get(FormField.FIRST_INITIAL_SIGN2)?.value || formFieldMap.get(FormField.LAST_INITIAL_SIGN2)?.value) {
      setIsShowCategory(true);
    } else {
      if (!formFieldMap.get(FormField.FIRST_INITIAL_SIGN2)?.value && !formFieldMap.get(FormField.LAST_INITIAL_SIGN2)?.value) {
        return;
      }

      setIsShowCategory(false);
      // remove warfarin when no signature 2
      const warfarinField = formFieldMap.get(FormField.WARFARIN);
      const markAllField = formFieldMap.get(FormField.MARK_ALL_THAT_APPLY);
      if (!warfarinField?.value) {
        return;
      }
      const chartContentWithWarfarinSelected = markAllField?.chartContent.reduce((acc, curr) => {
        if (curr.value === FormField.WARFARIN) {
          acc.push({ ...curr, selected: false });
        } else {
          acc.push(curr);
        }
        return acc;
      }, []);
      formFieldMap.set(FormField.WARFARIN, {
        ...warfarinField,
        chartContent: [{ ...warfarinField?.chartContent?.[0], selected: false }],
        value: ''
      });
      formFieldMap.set(FormField.MARK_ALL_THAT_APPLY, {
        ...markAllField,
        chartContent: chartContentWithWarfarinSelected
      });
    }
  }, [formFieldMap, formFieldMap.get(FormField.FIRST_INITIAL_SIGN2)?.value, formFieldMap.get(FormField.LAST_INITIAL_SIGN2)?.value]);

  useEffect(() => {
    const warfarin = document.querySelector('#field-input-warfarin-warfarin');
    const isWarfarinSelected = formFieldMap.get(FormField.WARFARIN)?.value.includes(FormField.WARFARIN);

    const timeOut = setTimeout(() => {
      if (formFieldMap.get(FormField.MARK_ALL_THAT_APPLY)?.value.includes('Warfarin') && warfarin instanceof HTMLElement && !isWarfarinSelected) {
        warfarin.click();
      }

      if (
        !formFieldMap.get(FormField.MARK_ALL_THAT_APPLY)?.value.includes('Warfarin') &&
        formFieldMap.get(FormField.WARFARIN)?.value &&
        warfarin instanceof HTMLElement &&
        isWarfarinSelected
      ) {
        warfarin.click();
      }
    }, 100);

    return () => clearTimeout(timeOut);
  }, [formFieldMap.get(FormField.MARK_ALL_THAT_APPLY)?.value]);

  useEffect(() => {
    if (!openSidebar) {
      setIsShowCategory(false);
    }
  }, [openSidebar]);

  const handleSlowReleaseChange = () => {
    const slowReleaseField = formFieldMap.get(FormField.SLOW_RELEASE);
    const toggleValue = slowReleaseField?.value ? '' : FormField.SLOW_RELEASE;
    const toggleSelected = !slowReleaseField?.chartContent[0].selected;
    formFieldMap.set(FormField.SLOW_RELEASE, {
      ...slowReleaseField,
      chartContent: [{ ...slowReleaseField.chartContent[0], selected: toggleSelected }],
      value: toggleValue
    });
  };

  const handleWarfarinChange = (target) => {
    const warfarinField = formFieldMap.get(FormField.WARFARIN);
    const markAllField = formFieldMap.get(FormField.MARK_ALL_THAT_APPLY);

    if (target.value === '') {
      const toggleValue = warfarinField?.value ? '' : FormField.WARFARIN;
      const toggleSelected = !warfarinField?.chartContent[0].selected;
      formFieldMap.set(FormField.WARFARIN, {
        ...warfarinField,
        chartContent: [{ ...warfarinField.chartContent[0], selected: toggleSelected }],
        value: toggleValue
      });
    }

    const chartContentWithWarfarinSelected = markAllField?.chartContent.reduce((acc, curr) => {
      if (curr.value === FormField.WARFARIN) {
        acc.push({ ...curr, selected: !target.value });
      } else {
        acc.push(curr);
      }
      return acc;
    }, []);

    // Handle check the warfarin on dropdown
    if (!target.value) {
      formFieldMap.set(FormField.MARK_ALL_THAT_APPLY, {
        ...markAllField,
        chartContent: chartContentWithWarfarinSelected
      });
    }

    // Handle un-check the warfarin on dropdown
    if (target.value.includes && target.value.includes(FormField.WARFARIN)) {
      formFieldMap.set(FormField.MARK_ALL_THAT_APPLY, {
        ...markAllField,
        chartContent: chartContentWithWarfarinSelected
      });
    }
  };

  const handleRadioChangeEvent = (target) => {
    if (target.name === FormField.SLOW_RELEASE) {
      handleSlowReleaseChange();
    }

    if (target.name === FormField.WARFARIN) {
      handleWarfarinChange(target);
    }
  };

  const handleOnCloseSidebar = () => {
    onCloseClick();
    setIsShowCategory(false);
  };

  return (
    <Sidebar
      open={doseFormMode === DOSE_MODE.ADMINISTER && openSidebar}
      onClose={() => chartHelper.handleCloseSidebar({ sidebarProps, onCloseClick: handleOnCloseSidebar })}
      sidebarProps={sidebarProps}
    >
      <Box mt3>
        <h2>Administer</h2>
        <h3 className="u-els-margin-top u-els-margin-bottom-3o4">{drugName}</h3>
        {[route, dose, frequency].filter(Boolean).length > 0 && <span className="u-els-bold u-els-margin-right">{[route, dose, frequency].filter(Boolean).join(' · ')}</span>}
        {specialInstructions && <span className="u-els-italic u-els-color-n7">{specialInstructions}</span>}
      </Box>
      <SectionDivider />
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} className="u-els-margin-bottom" />
      <Box className="mar-dosage">
        <ELSFlex left wrap>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.DOSAGE_DATE)}
              formSubmittedCount={formSubmittedCount}
              className="u-els-margin-bottom-1x1o2"
              inputProps={{ isDisabled: true }}
            />
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.DOSAGE_TIME)}
              formSubmittedCount={formSubmittedCount}
              className="u-els-margin-bottom-1x1o2"
              inputProps={{ disabled: true }}
            />
          </ELSFlexItem>
        </ELSFlex>

        <ELSFlex left wrap>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.ADMINISTERED_DATE)}
              formSubmittedCount={formSubmittedCount}
              className="u-els-margin-bottom-1x1o2"
              inputProps={{ isDisabled: true }}
            />
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.ADMINISTERED_TIME)}
              formSubmittedCount={formSubmittedCount}
              className="u-els-margin-bottom-1x1o2"
              inputProps={{ disabled: isAdministered }}
            />
          </ELSFlexItem>
        </ELSFlex>
        <ErrorFormField
          useDefaultMargin={false}
          formField={formFieldMap.get(FormField.ADMINISTERED_TIME_OFFSET)}
          formSubmittedCount={formSubmittedCount}
          className="u-els-margin-bottom-1x1o2 mar-dosage__input-offset-time"
          inputProps={{ isDisabled: isAdministered }}
        />
        <ErrorFormField
          formField={formFieldMap.get(FormField.COMMENTS)}
          formSubmittedCount={formSubmittedCount}
          useDefaultMargin={false}
          className="u-els-margin-bottom-3x"
          inputProps={{ disabled: isAdministered }}
        />
        <div className="u-els-margin-bottom-1x1o2">Signature 1</div>
        <ELSFlex className="o-els-flex-layout-gutters-1o2">
          <ELSFlexItem>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.FIRST_INITIAL)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2 u-els-margin-right-2x"
              inputProps={{ isDisabled: isAdministered }}
            />
          </ELSFlexItem>
          <ELSFlexItem grow>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.LAST_INITIAL)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2"
              inputProps={{ isDisabled: isAdministered }}
            />
          </ELSFlexItem>
        </ELSFlex>
        <ELSFlex className="mar-dosage__admission" spaceBetween>
          <ELSFlexItem>
            <div className="u-els-margin-bottom-1x1o2">Signature 2</div>
          </ELSFlexItem>
          {isShowCategory && (
            <ELSFlexItem>
              <div className="u-els-margin-bottom-1x1o2 u-els-margin-left-1o2">Admission Category</div>
            </ELSFlexItem>
          )}
        </ELSFlex>
      </Box>
      <Box className="mar-dosageSignatureTwo u-els-margin-bottom-3x">
        <ELSFlex className="o-els-flex-layout-gutters-3o4">
          <ELSFlexItem>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.FIRST_INITIAL_SIGN2)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2 u-els-margin-right-2x"
              inputProps={{ isDisabled: isAdministered }}
            />
          </ELSFlexItem>
          <ELSFlexItem grow>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(FormField.LAST_INITIAL_SIGN2)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2 u-els-margin-right-4x"
              inputProps={{ isDisabled: isAdministered }}
            />
          </ELSFlexItem>
          {isShowCategory && (
            <ELSFlexItem>
              <ErrorFormField
                useDefaultMargin={false}
                formField={formFieldMap.get(FormField.MARK_ALL_THAT_APPLY)}
                formSubmittedCount={formSubmittedCount}
                className="u-els-margin-bottom-1x1o2 mar-dosageSignatureTwo__category"
                inputProps={{ isDisabled: isAdministered }}
              />
            </ELSFlexItem>
          )}
        </ELSFlex>
      </Box>

      <ErrorFormField
        formField={formFieldMap.get(FormField.SLOW_RELEASE) ? { ...cloneDeep(formFieldMap.get(FormField.SLOW_RELEASE)), disabled: isAdministered } : undefined}
        formSubmittedCount={formSubmittedCount}
        inputProps={{ containerClassName: 'u-els-margin-top-1x u-els-margin-left-2x mar-showRelease' }}
        onChange={handleRadioChangeEvent}
      />
      {formFieldMap.get(FormField.SLOW_RELEASE)?.value && (
        <div className="mar-showRelease__note">
          <span>Note: </span>
          <ul style={{ paddingLeft: 40 }}>
            <li>
              <span className="prefix u-els-margin-right-1o4">•</span>
              <span>Sustained, modified or controlled release form.</span>
            </li>
            <li>
              <span className="prefix u-els-margin-right-1o4">•</span>
              <span>If tablet is scored, then half can be given.</span>
            </li>
            <li>
              <span className="prefix u-els-margin-right-1o4">•</span>
              <span>Dose must be swallowed without crushing.</span>
            </li>
          </ul>
        </div>
      )}

      <ErrorFormField
        useDefaultMargin={false}
        formField={formFieldMap.get(FormField.WARFARIN) ? { ...cloneDeep(formFieldMap.get(FormField.WARFARIN)), disabled: isAdministered } : undefined}
        formSubmittedCount={formSubmittedCount}
        inputProps={{ containerClassName: 'u-els-margin-top-1x u-els-margin-left-2x' }}
        onChange={handleRadioChangeEvent}
      />

      {formFieldMap.get(FormField.WARFARIN)?.value && (
        <ErrorFormField
          className="mar-inr"
          useDefaultMargin={false}
          formField={formFieldMap.get(FormField.INR)}
          formSubmittedCount={formSubmittedCount}
          inputProps={{ isDisabled: isAdministered }}
        />
      )}

      <ChartPermission>{({ allowSaveFragment }) => allowSaveFragment && !isAdministered && <ChartActions {...chartActionsProps} />}</ChartPermission>
    </Sidebar>
  );
};

MarDosageSidebarViewANZ.displayName = 'MarDosageSidebarViewANZ';
export default MarDosageSidebarViewANZ;
